/**
 * Custom round builder — pure stage-list logic (#1320, M008.8 2b).
 *
 * A custom round is an editable list of STAGES. Each stage:
 *   { distance, unit, ends, arrows_per_end, face, label? }
 *
 * A single stage is the common case (a normal single-distance round); the
 * builder defaults to one stage so that path behaves exactly as today. These
 * helpers are side-effect-free so they can be unit-tested without React, and
 * they mirror the backend (`app/custom_rounds.py`) for the mixed-scoring guard
 * and the live "N stages · M ends · X max" echo.
 */

import faceDefinitions from "../../../shared/face-definitions.json";
import { faceForDistance } from "./formatMatching";

// Coarse FACE mixing-family signature, sourced from
// shared/face-definitions.json `faces[*].mixing_family` (issue #1680 PR 2) —
// mirrors backend `mixing_family_for_face` (backend/app/face_definitions.py).
// Two faces can coexist on one scorecard iff their signatures match. 3D faces
// form their own family ("THREE_D"); everything else groups by its
// scoring-zone count (e.g. "RING_10" WA, "RING_5" NFAA, "RING_9" imperial).
// NOT the round-type-level `scoring_family` ("WA_TEN_RING", "NFAA_FIVE_RING",
// …) that roundConstants.js filters on — different enum, same old name (#2042).
const { faceTypes, faces } = faceDefinitions;

// The wizard used to carry "indoor40" / "outdoor" pre-selection sentinels here,
// which are not real face codes and had no entry in the shared JSON. They were
// replaced by their post-migration-0080 equivalents wa40 / wa122 (#2115) once
// target_face became registry-constrained at the API boundary. Any face still
// unknown to the JSON falls through to the RING_10 / max-arrow-10 default.

function faceTypeEntry(face) {
  const faceType = faces[face]?.face_type;
  return faceType ? faceTypes[faceType] : null;
}

export function mixingFamilyForFace(face) {
  return faces[face]?.mixing_family ?? "RING_10";
}

/**
 * Reverse-resolve a face registry CODE from its `face_type` + `face_size_cm`
 * (both stored server-side on a v2 segment, alongside a legacy segment's own
 * `face`). Used by `stagesFromPreset` (Finding 2, #2321 final review) so a
 * legal two-face preset — e.g. a WA 1440 with a wa122 stage and a wa40
 * stage — doesn't collapse both stages to the round type's single `rt.face`
 * just because neither segment carries an explicit v3 `faces` array.
 *
 * Round 2 (Finding A): `face_type` + `face_size_cm` is NOT always unique —
 * `vegas` and `3spot-tri` are both wa_triple/40cm, so a bare "first match"
 * silently renamed every 3spot-tri stage to vegas on load. `preferred`
 * (a face code, or a priority-ordered list of them — the Format's
 * `allowed_faces` then its own `rt.face`) disambiguates: the first hint that
 * is among the candidates for this face_type+size wins (migration 0076
 * dropped the scalar per-segment `face`, so these are the only surviving
 * hints); otherwise the first candidate. The result is ALWAYS a candidate:
 * an earlier version returned a non-matching `preferred` verbatim on a tie,
 * so a wa_triple/40 stage on a Format whose `face` was wa40 reloaded as
 * "wa40" — i.e. as the primary — and the editor's "Same as Format" collapse
 * silently erased the stage's override (#2408 sandbox QA). Returns null when
 * NO registry entry matches at all (unknown layout, or a null size for a
 * sized face).
 */
export function faceCodeFor(faceType, sizeCm, preferred) {
  if (!faceType) return null;
  const candidates = Object.keys(faces).filter((c) => {
    const def = faces[c];
    if (def.face_type !== faceType) return false;
    return sizeCm == null ? def.face_size_cm == null : Number(def.face_size_cm) === Number(sizeCm);
  });
  if (candidates.length === 0) return null;
  const hints = (Array.isArray(preferred) ? preferred : [preferred]).filter((h) => h != null);
  return hints.find((h) => candidates.includes(h)) ?? candidates[0];
}

/** Max points per arrow for a face — the X-ring / top-ring value. */
export function maxArrowForFace(face) {
  return faceTypeEntry(face)?.max_arrow ?? 10;
}

/**
 * A fresh single stage seeded from the wizard's current selection. `face`
 * defaults from `distance` via the shared `faceForDistance` policy (#2309) —
 * previously hardcoded to "wa40" regardless of distance, so an outdoor stage
 * created with no explicit face (e.g. the mount-time seed, or a preset with no
 * `face`) silently got the small indoor face instead of the WA outdoor face.
 */
export function defaultStage({ distance = 18, unit = "m", ends = 10, arrows_per_end = 3, face = faceForDistance(distance), face_size_cm = null } = {}) {
  return { distance, unit, ends, arrows_per_end, face, face_size_cm, label: null, alt_distances: [], faces: [] };
}

export function hasAltDistances(stages) {
  return stages.some((s) => Array.isArray(s.alt_distances) && s.alt_distances.length > 0);
}

/**
 * A deep-enough copy of one stage/target row for "duplicate" (#2400): the
 * `alt_distances` and `faces` ARRAYS are copied, not shared, so editing the
 * copy can never mutate the original (or vice versa).
 */
export function cloneStage(stage) {
  return {
    ...stage,
    alt_distances: [...(stage?.alt_distances || [])],
    faces: [...(stage?.faces || [])],
  };
}

/**
 * Split ONE stage into `n` same-distance breaks (#1320). The breaks inherit the
 * source stage's distance / unit / face / arrows; its ends are divided evenly
 * (remainder front-loaded), so the total ends are preserved. n=1 collapses back
 * to a single stage. Returns the new stage array (source replaced by n breaks).
 */
export function splitStageIntoBreaks(stages, index, n) {
  const src = stages[index];
  if (!src || n < 1) return stages;
  if (n === 1) {
    // Merge any existing sibling breaks of this stage back into one.
    return stages;
  }
  const totalEnds = src.ends;
  const base = Math.floor(totalEnds / n);
  const remainder = totalEnds % n;
  const breaks = [];
  for (let i = 0; i < n; i++) {
    breaks.push({
      ...src,
      alt_distances: src.alt_distances ? [...src.alt_distances] : [],
      faces: src.faces ? [...src.faces] : [],
      ends: base + (i < remainder ? 1 : 0),
      label: `Break ${i + 1}`,
    });
  }
  return [...stages.slice(0, index), ...breaks, ...stages.slice(index + 1)];
}

/** Total ends across all stages. */
export function totalEnds(stages) {
  return stages.reduce((sum, s) => sum + (Number(s.ends) || 0), 0);
}

/** Total arrows across all stages (ends × arrows-per-end, per stage). */
export function totalArrows(stages) {
  return stages.reduce((sum, s) => sum + (Number(s.ends) || 0) * (Number(s.arrows_per_end) || 0), 0);
}

/** Maximum achievable score across all stages. */
export function maxScore(stages) {
  return stages.reduce(
    (sum, s) => sum + (Number(s.ends) || 0) * (Number(s.arrows_per_end) || 0) * maxArrowForFace(s.face),
    0,
  );
}

/**
 * Whether the stages mix incompatible scoring families (review #5). True when
 * more than one family is present — such a round is blocked at build time.
 */
export function hasMixedScoring(stages) {
  const families = new Set(stages.map((s) => mixingFamilyForFace(s.face)));
  return families.size > 1;
}

/**
 * Validate a stage list for save/start. Returns { ok, message }. A single-stage
 * round with sane numbers is always ok. Blocks: empty, non-positive ends/arrows,
 * and mixed scoring families.
 */
export function validateStages(stages, opts = {}) {
  // `opts.noun` (#2400) is the archer-facing word for a row — "stage" for an
  // ends-based Format, "target" for a course. One list, one validator; only
  // the wording differs.
  const noun = opts.noun || "stage";
  const Noun = noun.charAt(0).toUpperCase() + noun.slice(1);
  if (!stages || stages.length === 0) return { ok: false, message: `Add at least one ${noun}.` };
  for (const s of stages) {
    if (!(Number(s.ends) > 0)) return { ok: false, message: `Each ${noun} needs at least one end.` };
    if (!(Number(s.arrows_per_end) > 0)) return { ok: false, message: `Each ${noun} needs at least one arrow per end.` };
  }
  if (hasMixedScoring(stages)) {
    return {
      ok: false,
      message: "This round mixes incompatible scoring (e.g. a 5-zone and a 10-zone break). A round can only use one scoring system.",
    };
  }
  // Round 2 (Finding C, F4 second half): the backend rejects any per-archer
  // distance pick once the stage list carries 2+ distinct nominal distances
  // (`per_archer_distance_unsupported`) — mirrors the Format editor's
  // `stagesMultiDistance` UI guard as a hard validation gate, so a caller
  // that builds a stage list some OTHER way (not just the editor's own
  // switch) can't submit this combination either.
  const distinctNominalDistances = new Set(stages.map((s) => s.distance).filter((d) => d != null));
  if (distinctNominalDistances.size > 1 && stages.some((s) => Array.isArray(s.alt_distances) && s.alt_distances.length > 0)) {
    return { ok: false, message: "Alternate distances need a single-distance format." };
  }
  const allowed = Array.isArray(opts.allowedFaces) ? new Set(opts.allowedFaces) : null;
  for (let i = 0; i < stages.length; i++) {
    const s = stages[i];
    const alts = (s.alt_distances || []).map(Number);
    if (new Set(alts).size !== alts.length || alts.some((a) => !(a > 0))) {
      return { ok: false, message: `${Noun} ${i + 1}: alternate distances must be distinct positive numbers.` };
    }
    if (s.distance != null && alts.includes(Number(s.distance))) {
      return { ok: false, message: `${Noun} ${i + 1}: the ${noun} distance is already the default, not an alternate.` };
    }
    if (allowed && (s.faces || []).some((f) => !allowed.has(f))) {
      return { ok: false, message: `${Noun} ${i + 1}: a ${noun} face must be one of the Format's faces.` };
    }
  }
  return { ok: true, message: "" };
}

/** Live one-line echo, e.g. "3 stages · 15 ends · 450 max". */
export function echoSummary(stages) {
  const n = stages.length;
  const label = n === 1 ? "1 stage" : `${n} stages`;
  return `${label} · ${totalEnds(stages)} ends · ${maxScore(stages)} max`;
}

/**
 * Live one-line echo INCLUDING the total arrow count (#2321) — e.g.
 * "1 stage · 5 ends · 30 arrows · 270 max". The Format editor's binding review
 * comment specifies this exact 4-part shape; `echoSummary` above stays
 * unchanged (3-part, no arrow count) because it is the wizard's established
 * copy and other surfaces already key off it. A new function rather than an
 * added parameter, so neither caller has to opt in/out of the other's shape.
 */
export function echoSummaryWithArrows(stages) {
  const n = stages.length;
  const label = n === 1 ? "1 stage" : `${n} stages`;
  return `${label} · ${totalEnds(stages)} ends · ${totalArrows(stages)} arrows · ${maxScore(stages)} max`;
}

/**
 * Build the inline `segments` array for POST /api/rounds (and the personal
 * round-type `segments`). Each stage maps to a v2 SegmentSpecIn; the backend
 * normalizer is tolerant, but we emit the canonical v2 shape directly.
 */
export function stagesToSegments(stages) {
  return stages.map((s) => {
    const faces = Array.isArray(s.faces) ? s.faces.filter(Boolean) : [];
    const alts = Array.isArray(s.alt_distances) ? s.alt_distances.filter((d) => d != null) : [];
    const seg = {
      distance: s.distance ?? null,
      unit: s.unit || "m",
      ends: Number(s.ends) || 0,
      arrows_per_end: Number(s.arrows_per_end) || null,
      face: faces[0] || s.face || null,
      label: s.label || null,
    };
    if (alts.length) seg.alt_distances = alts.map(Number);
    if (faces.length) seg.faces = faces;
    // A per-target face SIZE override (NFAA field/hunter course rows, #2436
    // finding 1 — those rule sets have only ONE face code, so a bigger/
    // smaller target can only be expressed as a per-segment `face_size_cm`,
    // never a different `face`). Omitted whenever unset so a row with no size
    // concept (ends mode, WA field, 3D) round-trips through this function
    // byte-for-byte as before.
    if (s.face_size_cm != null) seg.face_size_cm = s.face_size_cm;
    return seg;
  });
}

/**
 * Seed stages from a selected catalog preset's segment_template (so picking a
 * preset pre-fills an editable builder). Falls back to a single stage from the
 * round's distance/ends/arrows when the preset has no usable template.
 */
export function stagesFromPreset(rt, fallback) {
  const segs = Array.isArray(rt?.segment_template) ? rt.segment_template : null;
  if (segs && segs.length > 0) {
    const faceHints = Array.isArray(rt.allowed_faces) ? rt.allowed_faces : [];
    return segs.map((seg) => {
      const segFaces = Array.isArray(seg.faces) ? seg.faces : [];
      // Precedence (Finding 2, refined by Round 2 Finding A): the v3
      // per-stage `faces[0]` wins when present; otherwise re-derive the
      // stage's own face from its stored `face_type`/`face_size_cm` via the
      // registry, with the Format's `allowed_faces` (primary first) and then
      // `rt.face` as tie-breakers — a tied pair like vegas/3spot-tri (both
      // wa_triple/40cm) resolves to whichever the Format actually allows,
      // while a genuinely unique match (wa122 vs wa40) still resolves on its
      // own so a two-face preset doesn't collapse both stages to one face.
      const face = segFaces[0]
        || faceCodeFor(seg.face_type, seg.face_size_cm, [...faceHints, rt.face])
        || rt.face
        || fallback?.face
        || "wa40";
      return {
        distance: seg.distance ?? null,
        unit: seg.unit || "m",
        ends: Number(seg.ends) || 0,
        arrows_per_end: Number(seg.arrows_per_end) || rt.arrows_per_end || fallback?.arrows_per_end || 3,
        face,
        // A stored per-segment size override (NFAA field/hunter course
        // targets, #2436 finding 1) rides along so re-editing shows exactly
        // what was saved rather than re-deriving from the row's distance.
        face_size_cm: seg.face_size_cm ?? null,
        label: seg.label || null,
        alt_distances: Array.isArray(seg.alt_distances) ? [...seg.alt_distances] : [],
        faces: [...segFaces],
      };
    });
  }
  return [defaultStage(fallback)];
}
